import React, { useEffect, useState } from 'react';
import '../css/App.css';
import axios from 'axios';
import API_BASE from './apiBase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faEnvelope, faArrowLeft, faUsers, faUserCheck, faUserTimes, faSearch, faFilter, faHistory, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

/* Page Guide modal */
function GuideModal({ steps, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 16px', overflowY: 'auto',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620,
        border: '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>📖 Page Guide</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#94a3b8', fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '18px 20px', maxHeight: '75vh', overflowY: 'auto' }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, padding: '12px 0',
              borderBottom: i < steps.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}>
              <div style={{
                flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                background: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>{s.title}</div>
                <div style={{ fontSize: 12.5, color: '#4b5563', lineHeight: 1.5 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function UserManagementPage({ token, tenantId, onAddUser, onEditUser, onDeleteUser, onEmailUser, onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [tenantLimit, setTenantLimit] = useState(null);
  const [tenantUsed, setTenantUsed] = useState(null);
  const [selectedCard, setSelectedCard] = useState("all");
  const [guideOpen, setGuideOpen] = useState(false);


  const GUIDE_STEPS = [
  { title: 'User management', body: 'Manage organization accounts, roles, access, and account status from this section.', target: '.page-header-section' },
  { title: 'Add User', body: 'Click this button to add a new user — you will be asked for their email and full name.', target: '.add-user-button' },
  { title: 'Back', body: 'Use this button to go back to the tenant/dashboard view.', target: '.back-button' },
  { title: 'User stats cards', body: 'These cards show total, active, and inactive user counts. Click a card to filter the table below.', target: '.stats-grid' },
  { title: 'Search users', body: 'Search for a user by name or email using this box.', target: '.search-box' },
  { title: 'Filter status', body: 'Use this dropdown to filter the list by active or inactive users.', target: '.filter-box' },
  { title: 'Edit user', body: "Click the pencil icon to update a user's full name.", target: '.edit-action' },
  { title: 'Reset password', body: 'Click the envelope icon to reset and email a new password to this user.', target: '.email-action' },
  { title: 'Activity logs', body: "Click the history icon to view this user's audit/activity logs.", target: '.logs-action' },
  { title: 'Delete user', body: 'Click this icon to permanently remove the user account.', target: '.delete-action' },
];

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/manager/users?tenant_id=${tenantId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenant = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/tenants`, { headers: { Authorization: `Bearer ${token}` } });
      const tenant = res.data.find(t => t.id === tenantId);
      if (tenant) {
        setTenantLimit(tenant.limit || tenant.limits);
        setTenantUsed(tenant.used);
      }
    } catch (e) {
      // ignore tenant fetch errors; list still works
    }
  };

  const getErrorDetail = (err) => {
    if (!err) return '';
    if (err.response?.data) {
      const data = err.response.data;
      if (typeof data === 'string') return data;
      if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
      if (typeof data.error === 'string' && data.error.trim()) return data.error;
      if (typeof data.message === 'string' && data.message.trim()) return data.message;
      if (data.detail && typeof data.detail === 'object') return JSON.stringify(data.detail);
      if (data.error && typeof data.error === 'object') return JSON.stringify(data.error);
      if (data.message && typeof data.message === 'object') return JSON.stringify(data.message);
    }
    if (err.message) return err.message;
    return '';
  };

  useEffect(() => {
    if (tenantId && token) {
      fetchUsers();
      fetchTenant();
    }
  }, [tenantId, token]);

  // ── Format last login timestamp nicely ──────────────────────────────────────
  const formatLastLogin = (raw) => {
    if (!raw) return 'Never logged in';
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return raw;
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch (_) { return raw; }
  };

  // ── Detect if a user is a manager ───────────────────────────────────────────
  const isManager = (user) =>
    (user.role || '').toLowerCase() === 'manager';

  const handleAddUser = async () => {
    if (tenantLimit !== null && tenantUsed !== null && tenantUsed >= tenantLimit) {
      Swal.fire({
        icon: 'warning',
        title: 'User Limit Reached',
        text: `This tenant can only have ${tenantLimit} users. Please delete a user before adding another.`,
        customClass: { popup: 'swal-popup-custom' }
      });
      return;
    }
    const { value: formValues } = await Swal.fire({
      title: '<div style="color: #1f2937; font-size: 1.5rem; font-weight: 700;">Add New User</div>',
      html:
        '<div style="text-align: left; padding: 0 1rem;">' +
        '<div style="margin-bottom: 1.5rem;">' +
        '<label style="display: block; margin-bottom: 0.5rem; color: #374151; font-weight: 600; font-size: 0.875rem;">EMAIL ADDRESS</label>' +
        '<input id="swal-email" class="swal2-input" placeholder="user@example.com" style="margin: 0; width: 100%; padding: 0.75rem; border: 2px solid #e5e7eb; border-radius: 8px; font-size: 0.95rem;">' +
        '</div>' +
        '<div style="margin-bottom: 1rem;">' +
        '<label style="display: block; margin-bottom: 0.5rem; color: #374151; font-weight: 600; font-size: 0.875rem;">FULL NAME</label>' +
        '<input id="swal-name" class="swal2-input" placeholder="John Doe" style="margin: 0; width: 100%; padding: 0.75rem; border: 2px solid #e5e7eb; border-radius: 8px; font-size: 0.95rem;">' +
        '</div>' +
        '</div>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: '<span style="padding: 0 1rem;">Add User</span>',
      cancelButtonText: 'Cancel',
      width: '500px',
      customClass: {
        popup: 'swal-popup-custom',
        confirmButton: 'swal-confirm-btn',
        cancelButton: 'swal-cancel-btn'
      },
      buttonsStyling: false,
      preConfirm: () => {
        const email = document.getElementById('swal-email').value;
        const name = document.getElementById('swal-name').value;
        if (!email || !name) { Swal.showValidationMessage('Please fill in all fields'); return false; }
        if (!/\S+@\S+\.\S+/.test(email)) { Swal.showValidationMessage('Please enter a valid email'); return false; }
        return { email, full_name: name };
      }
    });
    if (!formValues) return;
    try {
      setLoading(true);
      const createRes = await axios.post(`${API_BASE}/manager/create-user`, {
        email: formValues.email,
        full_name: formValues.full_name,
        tenant_id: tenantId
      }, { headers: { Authorization: `Bearer ${token}` } });
      const data = createRes.data || {};
      if (data.emailed === false && data.default_password) {
        await Swal.fire({
          icon: 'success', title: 'User created',
          html: `<div style="text-align:left">User created successfully.<br><br><strong>Password:</strong> <code>${data.default_password}</code><br><br>Please copy this password and share securely with the user (email delivery failed).</div>`,
          customClass: { popup: 'swal-popup-custom' }
        });
      } else {
        Swal.fire({ icon: 'success', title: 'Success!', text: 'User created successfully', timer: 2000, showConfirmButton: false, customClass: { popup: 'swal-popup-custom' } });
      }
      await fetchUsers();
      await fetchTenant();
    } catch (err) {
      const detail = getErrorDetail(err);
      if (err?.response?.status === 400 && /email/i.test(detail) && /exist/i.test(detail)) {
        Swal.fire({ icon: 'error', title: 'Duplicate Email', text: detail || 'A user with this email already exists.', customClass: { popup: 'swal-popup-custom' } });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: detail || 'Failed to create user', customClass: { popup: 'swal-popup-custom' } });
      }
    } finally { setLoading(false); }
  };

  const handleEditUser = async (user) => {
    // Managers cannot be edited from this page
    if (isManager(user)) return;

    const { value: formValues } = await Swal.fire({
      title: '<div style="color: #1f2937; font-size: 1.5rem; font-weight: 700;">Edit User</div>',
      html:
        '<div style="text-align: left; padding: 0 1rem;">' +
        '<div style="margin-bottom: 1.5rem;">' +
        '<label style="display: block; margin-bottom: 0.5rem; color: #374151; font-weight: 600; font-size: 0.875rem;">FULL NAME</label>' +
        `<input id="swal-name" class="swal2-input" placeholder="Full name" value="${user.full_name || ''}" style="margin: 0; width: 100%; padding: 0.75rem; border: 2px solid #e5e7eb; border-radius: 8px; font-size: 0.95rem;">` +
        '</div>' +
        '</div>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: '<span style="padding: 0 1rem;">Update User</span>',
      cancelButtonText: 'Cancel',
      width: '500px',
      customClass: { popup: 'swal-popup-custom', confirmButton: 'swal-confirm-btn', cancelButton: 'swal-cancel-btn' },
      buttonsStyling: false,
      preConfirm: () => ({ full_name: document.getElementById('swal-name').value })
    });
    if (!formValues) return;
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/manager/edit-user/${user.id}`, {
        full_name: formValues.full_name, is_active: user.is_active
      }, { headers: { Authorization: `Bearer ${token}` } });
      Swal.fire({ icon: 'success', title: 'Updated!', text: 'User updated successfully', timer: 2000, showConfirmButton: false, customClass: { popup: 'swal-popup-custom' } });
      const res = await axios.get(`${API_BASE}/manager/users?tenant_id=${tenantId}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.detail || 'Failed to update user', customClass: { popup: 'swal-popup-custom' } });
    } finally { setLoading(false); }
  };

  const handleDeleteUser = async (user) => {
    // Managers cannot be deleted from this page
    if (isManager(user)) return;
    const r = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete:<br><strong style="color: #dc2626;">${user.email}</strong><br><br>This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      focusCancel: true,
      buttonsStyling: false,
      didOpen: () => {
        const confirmBtn = Swal.getConfirmButton();
        const cancelBtn = Swal.getCancelButton();
        const actions = Swal.getActions();

        // Fix spacing between OK and Cancel — this is the actual root cause
        if (actions) {
          Object.assign(actions.style, {
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '20px',
          });
        }

        if (confirmBtn) Object.assign(confirmBtn.style, {
          display: 'inline-block',
          background: 'rgb(220, 38, 38)',
          color: 'rgb(255, 255, 255)',
          borderWidth: 'medium',
          borderStyle: 'none',
          borderColor: 'currentcolor',
          borderImage: 'none',
          padding: '10px 28px',
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: '1rem',
          cursor: 'pointer',
          margin: '0',           // let the flex `gap` handle spacing, not margin
        });

        if (cancelBtn) Object.assign(cancelBtn.style, {
          display: 'inline-block',
          background: '#f3f4f6',
          color: '#374151',
          border: '1px solid #d1d5db',
          padding: '10px 28px',
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: '1rem',
          cursor: 'pointer',
          margin: '0',
        });
      },
    });
    if (!r.isConfirmed) return;
    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/manager/delete-user/${user.id}`, { headers: { Authorization: `Bearer ${token}` } });
      Swal.fire({ icon: 'success', title: 'Deleted!', text: 'User has been removed', timer: 2000, showConfirmButton: false, customClass: { popup: 'swal-popup-custom' } });
      await fetchUsers();
      await fetchTenant();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.detail || 'Failed to delete user', customClass: { popup: 'swal-popup-custom' } });
    } finally { setLoading(false); }
  };

  const handleEmailUser = async (user) => {
    if (!user || !user.id) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No user selected', customClass: { popup: 'swal-popup-custom' } });
      return;
    }

    const confirmation = await Swal.fire({
      title: 'Reset password?',
      html: `A new password will be generated and emailed to <strong>${user.email}</strong>. Do you want to continue?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, reset password',
      cancelButtonText: 'Cancel',
      customClass: { popup: 'swal-popup-custom', confirmButton: 'swal-confirm-btn', cancelButton: 'swal-cancel-btn' },
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) return;

    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/manager/reset-user-password`, { user_id: user.id }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      const data = res.data || {};
      if (data.emailed) {
        Swal.fire({ icon: 'success', title: 'Password Reset & Sent', html: `<div style="text-align:left">A new password has been generated and emailed to <strong>${user.email}</strong>.</div>`, customClass: { popup: 'swal-popup-custom' } });
      } else if (data.new_password) {
        Swal.fire({ icon: 'warning', title: 'Email Failed', html: `<div style="text-align:left">A new password was generated but email delivery failed.<br><br><strong>Password:</strong> <code>${data.new_password}</code><br><br>Please copy this password and share securely with the user.</div>`, customClass: { popup: 'swal-popup-custom' } });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to reset password or send email', customClass: { popup: 'swal-popup-custom' } });
      }
      const usersRes = await axios.get(`${API_BASE}/manager/users?tenant_id=${tenantId}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(usersRes.data);
    } catch (err) {
      let detail = err?.response?.data?.detail;
      if (typeof detail === 'object') detail = JSON.stringify(detail);
      Swal.fire({ icon: 'error', title: 'Error', text: detail || (err?.message ? String(err.message) : 'Failed to reset password or send email'), customClass: { popup: 'swal-popup-custom' } });
    } finally { setLoading(false); }
  };

  const openLogsForUser = async (user) => {
    if (!user || !user.email) return;
    const tenant = tenantId;
    const apiBaseClean = API_BASE ? String(API_BASE).replace(/\/$/, '') : '';
    const candidates = new Set();
    if (apiBaseClean) {
      candidates.add(`${apiBaseClean}/manager/audit-logs`);
      if (!apiBaseClean.endsWith('/api')) candidates.add(`${apiBaseClean}/api/manager/audit-logs`);
      else candidates.add(`${apiBaseClean.replace(/\/$/, '')}/manager/audit-logs`);
    }
    candidates.add('/manager/audit-logs');
    candidates.add('/api/manager/audit-logs');

    const apiKey = process.env.REACT_APP_MANAGER_API_KEY || localStorage.getItem('manager_api_key') || '';
    const apiToken = process.env.REACT_APP_MANAGER_API_TOKEN || localStorage.getItem('manager_api_token') || '';
    const headers = {};
    if (apiKey) headers['X-API-KEY'] = apiKey;
    if (apiToken) headers['X-API-TOKEN'] = apiToken;

    let respData = null;
    for (const urlRoot of Array.from(candidates)) {
      try {
        const sep = urlRoot.includes('?') ? '&' : '?';
        const url = `${urlRoot}${sep}tenant_id=${encodeURIComponent(tenant || '')}&email=${encodeURIComponent(user.email)}`;
        const res = await fetch(url, { headers });
        if (res.status === 401) throw new Error('Unauthorized');
        if (res.status < 200 || res.status >= 300) continue;
        const text = await res.text();
        if (!text) respData = null;
        else respData = JSON.parse(text);
        break;
      } catch (e) { continue; }
    }

    const logs = (respData && (respData.logs || respData)) || [];
    if (!logs || logs.length === 0) {
      Swal.fire({ icon: 'info', title: 'No logs', text: `No audit logs found for ${user.email}` });
      return;
    }

    const rows = logs.map(l => {
      const ts = l.timestamp_ist || l.timestamp || l.createdAt || l.time || '';
      // Show only Indian Standard Time (IST)
      let tsFormatted = ts;
      try {
        const raw = String(ts || '').trim();
        if (raw && !l.timestamp_ist) {
          let parseable = raw;
          if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
            parseable = raw.replace(' ', 'T') + 'Z';
          } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(raw)) {
            parseable = `${raw}Z`;
          }
          const d = new Date(parseable);
          if (!Number.isNaN(d.getTime())) {
            const ist = d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
            tsFormatted = ist;
          }
        }
      } catch (_) { }

      let detailsText = '';
      let parsedDetails = null;
      try {
        let d = l.details;
        if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { } }
        if (d && typeof d === 'object') {
          const body = d.body || d.request || d;
          if (body && typeof body === 'object') {
            if (body.details) detailsText = String(body.details);
            else if (body.action) detailsText = String(body.action);
            else { const parts = []; Object.keys(body).forEach(k => { try { const v = body[k]; if (v === null || v === undefined) return; if (typeof v === 'object') parts.push(`${k}: ${JSON.stringify(v)}`); else parts.push(`${k}: ${String(v)}`); } catch (e) { } }); detailsText = parts.join('\n'); }
          } else if (d.details) detailsText = String(d.details);
          else detailsText = JSON.stringify(d);
        } else detailsText = String(d || '');
        parsedDetails = d;
      } catch (e) { detailsText = String(l.details || ''); }

      const headerParts = [];
      try {
        const actor = l.actor_email || l.actor || (parsedDetails && parsedDetails.actor_email) || null;
        const target = l.target_email || l.target || (parsedDetails && parsedDetails.target_email) || null;
        if (actor) headerParts.push(`Actor: ${actor}`);
        if (target) headerParts.push(`Target: ${target}`);

        // Prefer explicit IP fields from the row or parsed details; fall back to text match
        let ipVal = l.ip_address || l.ip || l.client_ip || (parsedDetails && (parsedDetails.ip || parsedDetails.ip_address || parsedDetails.session_ip)) || null;
        if (!ipVal) {
          const ipMatch = detailsText.match(/(?:\bip\b|ip_address)\s*=?\s*([0-9a-fA-F:.]+)/i);
          if (ipMatch) ipVal = ipMatch[1];
        }
        if (ipVal) headerParts.push(`IP: ${ipVal}`);

        const sessionMatch = detailsText.match(/session_version\s*=?\s*(\d+)/i);
        if (sessionMatch) headerParts.push(`Session ver: ${sessionMatch[1]}`);
      } catch (e) { }

      try {
        const savedMatch = detailsText.match(/Saved consent\s*id\s*=?\s*(\d+)\s*(?:type\s*=\s*([\w-]+))?/i);
        if (savedMatch) {
          const ctype = savedMatch[2] || '';
          let actor = l.actor_email || l.actor || (parsedDetails && (parsedDetails.actor_email || parsedDetails.actor));
          let uname = (parsedDetails && (parsedDetails.user_name || parsedDetails.username || parsedDetails.name)) || null;
          if (!actor) actor = l.target_email || (parsedDetails && parsedDetails.target_email) || null;
          const userLine = uname ? `${uname}${actor ? ' · ' + actor : ''}` : (actor ? actor : '');
          detailsText = `Saved consent:${ctype ? '\n type: ' + ctype : ''}` + (userLine ? `\nUser: ${userLine}` : '');
        } else {
          const revokedMatch = detailsText.match(/Revoked fields\s*:?\s*(.+)/i);
          if (revokedMatch) {
            const items = (revokedMatch[1] || '').split(/,|\s,\s/).map(s => s.trim()).filter(Boolean);
            detailsText = `Revoked fields:\n- ${items.join('\n- ')}`;
          }
        }
      } catch (e) { }

      const headerText = headerParts.length ? headerParts.join(' · ') + '\n\n' : '';
      const detailsHtml = (headerText + detailsText + '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');

      return `<tr><td style="padding:8px;border-bottom:1px solid #eee;vertical-align:top;white-space:nowrap;font-size:0.85rem">${tsFormatted}</td><td style="padding:8px;border-bottom:1px solid #eee;vertical-align:top">${detailsHtml}</td></tr>`;
    }).join('');

    const tableHtml = `
      <div style="max-height:420px;overflow:auto;padding:8px 4px;text-align:left">
        <table style="width:100%;border-collapse:collapse;font-size:0.95rem">
          <thead><tr>
            <th style="text-align:left;padding:8px;border-bottom:2px solid #ddd;white-space:nowrap">Time (IST)</th>
            <th style="text-align:left;padding:8px;border-bottom:2px solid #ddd">Details</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;

    await Swal.fire({ title: `Activity logs: ${user.email}`, html: tableHtml, width: 900, showCloseButton: true, customClass: { popup: 'swal-popup-custom' } });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'active' && user.is_active) ||
      (filterStatus === 'inactive' && !user.is_active);
    let matchesCard = true;
    if (selectedCard === 'active') matchesCard = user.is_active === true;
    if (selectedCard === 'inactive') matchesCard = user.is_active === false;
    return matchesSearch && matchesFilter && matchesCard;
  });

  const activeUsers = users.filter(u => u.is_active).length;
  const inactiveUsers = users.length - activeUsers;

  if (loading) {
    return (
      <div className="user-management-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}><span>Retry</span></button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <div className="page-header-section">
        <div className="header-top">
          <div className="page-title-area">
            <div className="title-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div>
            <div className="title-content">
              <h1>User Management</h1>
              <p>Manage and monitor all users in your organization</p>
            </div>
          </div>
          <button className="add-user-button" onClick={handleAddUser}>
            <FontAwesomeIcon icon={faPlus} /><span>Add User</span>
          </button>
          <button className="back-button" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} /><span>Back</span>
          </button>
          <button
          className="add-user-button"
          onClick={() => setGuideOpen(true)}
          style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}
        >
          <span>📖</span><span>Page Guide</span>
        </button>

        </div>
      </div>

      {/* ── Stats cards ── */}
      <div className="stats-grid">
        {[
          { key: 'all', label: 'Total Users', count: users.length, icon: faUsers, cls: 'total-card', badge: 'All users', badgeCls: '' },
          { key: 'active', label: 'Active Users', count: activeUsers, icon: faUserCheck, cls: 'active-card', badge: 'Online', badgeCls: 'success' },
          { key: 'inactive', label: 'Inactive Users', count: inactiveUsers, icon: faUserTimes, cls: 'inactive-card', badge: 'Offline', badgeCls: 'warning' },
        ].map(({ key, label, count, icon, cls, badge, badgeCls }) => (
          <div key={key} className={`stat-card ${cls}`}
            onClick={() => setSelectedCard(key)}
            style={{ cursor: 'pointer', transform: selectedCard === key ? 'scale(1.03)' : 'scale(1)' }}>
            <div className={`stat-icon-box ${key}-icon`}><FontAwesomeIcon icon={icon} /></div>
            <div className="stat-info">
              <div className="stat-label">{label}</div>
              <div className="stat-number">{count}</div>
            </div>
            <div className="stat-trend">
              <span className={`trend-badge ${badgeCls}`}>{badge}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="content-card">
        <div className="content-header">
          <div className="search-filter-group">
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input type="text" placeholder="Search by name or email..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input" />
            </div>
            <div className="filter-box">
              <FontAwesomeIcon icon={faFilter} className="filter-icon" />
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>
          <div className="showing-results">Showing {filteredUsers.length} of {users.length} users</div>
        </div>

        <div className="table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th style={{ width: '60px', textAlign: 'center' }}>S.No</th>
                <th>User Information</th>
                <th>Last Login</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data-row">
                    <div className="no-data-wrapper">
                      <div className="no-data-icon"><FontAwesomeIcon icon={faUsers} /></div>
                      <h3>No users found</h3>
                      <p>{searchTerm ? 'Try adjusting your search or filter' : 'Get started by adding your first user'}</p>
                      {!searchTerm && (
                        <button className="add-first-btn" onClick={handleAddUser}>
                          <FontAwesomeIcon icon={faPlus} /><span>Add Your First User</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, idx) => {
                  const manager = isManager(user);
                  return (
                    <tr key={user.id || user.email}
                      className="user-row"
                      style={manager ? { background: 'linear-gradient(90deg, #eff6ff 0%, #f8faff 100%)', borderLeft: '3px solid #2563eb' } : {}}>

                      {/* S.No */}
                      <td data-label="S.No" style={{ textAlign: 'center', fontWeight: 600, color: '#64748b' }}>
                        {idx + 1}
                      </td>

                      {/* User Information */}
                      <td data-label="User Information">
                        <div className="user-info-cell">
                          {/* Avatar — blue for manager, grey for user */}
                          <div className="user-avatar"
                            style={manager ? { background: 'linear-gradient(135deg, #1d4ed8, #2563eb)', color: '#fff' } : {}}>
                            {(user.full_name || user.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="user-details">
                            <div className="user-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {user.full_name || 'No name'}
                              {/* Manager badge */}
                              {manager && (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  background: '#dbeafe', color: '#1d4ed8',
                                  fontSize: '0.7rem', fontWeight: 700,
                                  padding: '2px 8px', borderRadius: 20,
                                  border: '1px solid #93c5fd', letterSpacing: 0.3,
                                }}>
                                  <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '0.65rem' }} />
                                  MANAGER
                                </span>
                              )}
                              {/* ✅ User badge — manager nahi hai toh dikhao */}
                              {!manager && (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  background: '#f0fdf4', color: '#15803d',
                                  fontSize: '0.7rem', fontWeight: 700,
                                  padding: '2px 8px', borderRadius: 20,
                                  border: '1px solid #86efac', letterSpacing: 0.3,
                                }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.5"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                  </svg>
                                  USER
                                </span>
                              )}
                            </div>
                            <div className="user-email">{user.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Last Login — nicely formatted */}
                      <td data-label="Last Login">
                        <div className="last-login-cell">
                          {formatLastLogin(user.last_login)}
                        </div>
                      </td>

                      {/* Status ── managers: read-only pill; users: dropdown toggle */}
                      <td data-label="Status">
                        {manager ? (
                          // Managers: just show a static badge — no dropdown, no toggle
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '5px 14px', borderRadius: 20, fontWeight: 600, fontSize: '0.85rem',
                            background: user.is_active ? '#dcfce7' : '#fee2e2',
                            color: user.is_active ? '#15803d' : '#dc2626',
                          }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        ) : (
                          // Regular users: dropdown to activate / deactivate
                          <select
                            value={user.is_active ? 'true' : 'false'}
                            onChange={async (e) => {
                              const newStatus = e.target.value === 'true';
                              try {
                                await axios.put(
                                  `${API_BASE}/manager/edit-user/${user.id}`,
                                  { full_name: user.full_name, is_active: newStatus },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: newStatus } : u));
                                Swal.fire({
                                  icon: 'success', title: 'Status Updated',
                                  text: `User ${newStatus ? 'Activated' : 'Deactivated'} Successfully`,
                                  timer: 1500, showConfirmButton: false
                                });
                              } catch (err) {
                                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update status' });
                              }
                            }}
                            style={{
                              padding: '6px 12px', borderRadius: 20, fontWeight: 600,
                              border: 'none', cursor: 'pointer',
                              background: user.is_active ? '#dcfce7' : '#fee2e2',
                              color: user.is_active ? '#15803d' : '#dc2626'
                            }}>
                            <option value="true">🟢 Active</option>
                            <option value="false">🔴 Inactive</option>
                          </select>
                        )}
                      </td>

                      {/* Actions ── managers: no actions shown; users: full action set */}
                      <td data-label="Actions">
                        {manager ? (
                          // Manager row — no actions available
                          <span style={{ color: '#9ca3af', fontSize: '0.8rem', fontStyle: 'italic' }}>—</span>
                        ) : (
                          <div className="action-buttons-cell" style={{ display: 'flex', gap: '8px' }}>
                            <button className="action-button edit-action" onClick={() => handleEditUser(user)} title="Edit User"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '1.15rem', padding: '6px' }}>
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button className="action-button email-action" onClick={() => handleEmailUser(user)} title="Reset Password"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '1.15rem', padding: '6px' }}>
                              <FontAwesomeIcon icon={faEnvelope} />
                            </button>
                            <button className="action-button logs-action" onClick={() => openLogsForUser(user)} title="Activity Logs"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '1.15rem', padding: '6px' }}>
                              <FontAwesomeIcon icon={faHistory} />
                            </button>
                            <button className="action-button delete-action" onClick={() => handleDeleteUser(user)} title="Delete User"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: '1.15rem', padding: '6px' }}>
                              <FontAwesomeIcon icon={faUserTimes} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {guideOpen && (
        <GuideModal
          steps={GUIDE_STEPS}
          onClose={() => setGuideOpen(false)}
        />
      )}
    </div>
  );
}

export default UserManagementPage;